// src/pages/ProtectedRoute.tsx

/**
 * Route guard for authenticated pages in the Quantik Mind enterprise frontend.
 *
 * This component prevents access to protected routes when no authentication
 * token is available in the client session. When the token is missing, the
 * user is redirected to the login page.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../context/authContext';

/**
 * Protect a route element by checking whether an authentication token exists.
 *
 * @param element - React element to render when the user is authenticated.
 * @returns The protected element or a redirect to the login page.
 */
const ProtectedRoute = ({
  element,
  requireVerified = false,
}: {
  element: React.ReactElement;
  requireVerified?: boolean;
}) => {
  const { authData, authReady } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return null;
  }

  /**
   * Redirect unauthenticated users to the login page.
   */
  if (!authData) {
    return <Navigate to="/login" replace />;
  }

  if (requireVerified && !authData.emailVerified) {
    return (
      <Navigate
        to="/verify-email/check"
        replace
        state={{ email: authData.email, from: location.pathname }}
      />
    );
  }

  /**
   * Render the requested route when a token is available.
   */
  return element;
};

export default ProtectedRoute;
